import { apiClient } from './client'

export interface Lesson {
  id: string
  _id?: string // Some responses include _id
  title: string
  teacher: {
    id: string
    name: string
  }
  teachers?: Array<{
    id: string
    name: string
  }>
  description?: string
  duration: number
  objectives?: string
  materials?: string
  streams: Stream[]
  isActive?: boolean
  createdBy: string // User name of the creator
  createdAt: string
  updatedAt: string
}

interface Stream {
  id: string
  name: string
  grade: string
  studentCount: number
}

export interface LessonStats {
  totalLessons: number
  activeLessons: number
  inactiveLessons: number
  byTeacher: Record<string, number>
  byCreator: Record<string, number>
}

export interface CreateLessonRequest {
  title: string
  teacherId?: string // Single teacher (backward compatibility)
  teacherIds?: string[] // Multiple teachers
  description?: string
  duration: number
  objectives?: string
  materials?: string
  selectedStreams: string[] // Array of stream IDs
}

export interface UpdateLessonRequest {
  title?: string
  teacherId?: string // Single teacher (backward compatibility)
  teacherIds?: string[] // Multiple teachers
  description?: string
  duration?: number
  objectives?: string
  materials?: string
  selectedStreams?: string[]
  isActive?: boolean // For deactivation
}

export interface GetLessonsParams {
  teacherId?: string
  streamId?: string
  search?: string
  institutionId?: string // For super admin filtering
}

export interface LessonsResponse {
  success: boolean
  data: Lesson[]
  message: string
}

export interface LessonResponse {
  success: boolean
  data: Lesson
  message: string
}

export interface LessonStatsResponse {
  success: boolean
  data: LessonStats
  message: string
}

// Get all lessons with optional filtering
export const getLessons = async (params: GetLessonsParams = {}): Promise<LessonsResponse> => {
  const searchParams = new URLSearchParams()
  
  if (params.teacherId) searchParams.append('teacherId', params.teacherId)
  if (params.streamId) searchParams.append('streamId', params.streamId)
  if (params.search) searchParams.append('search', params.search)
  if (params.institutionId) searchParams.append('institutionId', params.institutionId)
  
  const queryString = searchParams.toString()
  const url = queryString ? `/academic/lessons?${queryString}` : '/academic/lessons'
  
  const response = await apiClient.get(url)
  return response.data
}

// Get single lesson by ID
export const getLessonById = async (lessonId: string): Promise<LessonResponse> => {
  const response = await apiClient.get(`/academic/lessons/${lessonId}`)
  return response.data
}

// Create new lesson
export const createLesson = async (lessonData: CreateLessonRequest): Promise<LessonResponse> => {
  const response = await apiClient.post('/academic/lessons', lessonData)
  return response.data
}

// Update lesson
export const updateLesson = async (lessonId: string, lessonData: UpdateLessonRequest): Promise<LessonResponse> => {
  const response = await apiClient.put(`/academic/lessons/${lessonId}`, lessonData)
  return response.data
}

// Delete lesson
export const deleteLesson = async (lessonId: string): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.delete(`/academic/lessons/${lessonId}`)
  return response.data
}

// Get lesson statistics
export const getLessonStats = async (): Promise<LessonStatsResponse> => {
  const response = await apiClient.get('/academic/lessons/stats')
  return response.data
}